import { CollisionPriority, CollisionType } from '@dnd-kit/abstract';
import { EDGE_ZONE_SIZE, MAX_LINK_LEVEL, UNIQUE_PER_PARENT_TYPES } from '../constants';
import { OwnerType, TBandInput, TOwner, TOwnerLink, TOwnershipStructure } from '../types';
import type { CollisionDetector, DragMoveEvent, DragOverEvent } from '@dnd-kit/abstract';
import { UseFormReturn } from 'react-hook-form';
import { DragDropManager } from '@dnd-kit/react';
import { v4 as uuidv4 } from 'uuid';

const getBands = ({ dragOperation, droppable }: TBandInput) => {
  const pointer = dragOperation.position?.current;
  const rect = droppable.shape?.boundingRectangle;
  if (!pointer || !rect) return null;

  const inside =
    pointer.x >= rect.left &&
    pointer.x <= rect.right &&
    pointer.y >= rect.top &&
    pointer.y <= rect.bottom;
  if (!inside) return null;

  const band = Math.min(EDGE_ZONE_SIZE, rect.height / 3);
  const distTop = pointer.y - rect.top;
  const distBottom = rect.bottom - pointer.y;

  return { distTop, distBottom, band };
};

const getLevel = (droppable: TBandInput['droppable']) => Number(droppable.data?.level ?? 0);

export const edgeBandCollision: CollisionDetector = (input) => {
  const bands = getBands(input);
  if (!bands) return null;

  const { distTop, distBottom, band } = bands;
  const withinEdgeBand = distTop <= band || distBottom <= band;

  if (withinEdgeBand) {
    return {
      id: input.droppable.id,
      value: 1 / (1 + Math.min(distTop, distBottom)),
      type: CollisionType.Collision,
      priority: CollisionPriority.High + getLevel(input.droppable),
    };
  }

  const data = input.droppable.data;
  const isParentCard = data?.isParentCard ?? data?.data?.isParentCard;
  if (!isParentCard) return null;

  const pointer = input.dragOperation?.position?.current;
  if (!pointer) return null;

  const sourceId = String(input.dragOperation?.source?.id ?? '');

  const stack = document.elementsFromPoint(pointer.x, pointer.y);
  let cardUnderPointer: string | null = null;
  for (const el of stack) {
    const id = el.closest('[data-link-id]')?.getAttribute('data-link-id');
    if (id && id !== sourceId) {
      cardUnderPointer = id;
      break;
    }
  }

  if (cardUnderPointer !== String(input.droppable.id)) return null;

  return {
    id: input.droppable.id,
    value: 0.001,
    type: CollisionType.Collision,
    priority: CollisionPriority.Normal + getLevel(input.droppable),
  };
};

export const applyDragPosition = (
  event: DragOverEvent | DragMoveEvent,
  manager: DragDropManager,
  {
    getValues,
    setValue,
    trigger,
  }: Pick<UseFormReturn<TOwnershipStructure>, 'getValues' | 'setValue' | 'trigger'>,
  onDropParentChange?: (parentLinkId: string | null, allowed: boolean) => void,
  handleCalculateOwnershipPercentage?: () => void
) => {
  const source = event.operation?.source ?? manager?.dragOperation?.source;
  const target = event.operation?.target ?? manager?.dragOperation?.target;

  if (!source || !target || source.id === target.id) return;

  const links = getValues('link');
  const owners = getValues('owners');

  const currentLinkIndex = links.findIndex((item) => item.id === source.id);
  const overLinkIndex = links.findIndex((item) => item.id === target.id);
  if (currentLinkIndex === -1 || overLinkIndex === -1) return;

  const resolveDisabledHighlight = (parentLinkId: string | null): string | null => {
    let current = newLinks.find((l) => l.id === parentLinkId);
    let result = parentLinkId;
    while (current) {
      if (current.fromNodeId === dragged.fromNodeId) {
        result = current.parentLinkId ?? current.id;
      }
      current = newLinks.find((l) => l.id === current?.parentLinkId);
    }
    return result;
  };

  const newLinks = links.map((link) => ({ ...link }));
  const dragged = newLinks[currentLinkIndex] ?? ({} as TOwnerLink);
  const over = newLinks[overLinkIndex] ?? ({} as TOwnerLink);

  if (over.fromNodeId === dragged.fromNodeId) {
    onDropParentChange?.(resolveDisabledHighlight(over.id), false);
    return;
  }
  let ancestor: TOwnerLink | null = over;
  while (ancestor) {
    if (ancestor.id === dragged.id) return;
    ancestor = newLinks.find((l) => l.id === ancestor?.parentLinkId) || null;
  }

  const pointerY =
    event.operation.position?.current?.y ?? manager?.dragOperation?.position?.current?.y ?? null;

  const rect =
    (target.id != null
      ? document.querySelector(`[data-link-id="${target.id}"]`)?.getBoundingClientRect()
      : null) ??
    target.shape?.boundingRectangle ??
    null;

  if (pointerY === null || rect === null) return;

  const EDGE = 10;
  const inTopZone = pointerY < rect.top + EDGE;
  const inBottomZone = pointerY > rect.bottom - EDGE;
  const pointerInUpperHalf = pointerY < rect.top + rect.height / 2;

  const targetOwner = owners.find((owner) => owner.id === over.fromNodeId);
  const draggedOwner = owners.find((owner) => owner.id === dragged.fromNodeId);
  const overParentLink = newLinks.find((l) => l.id === over.parentLinkId);
  const overParentOwner = owners.find((o) => o.id === overParentLink?.fromNodeId);
  const overSitsInIndividual = overParentOwner?.type === OwnerType.INDIVIDUAL;

  const overIsParent =
    (targetOwner?.type === OwnerType.LEGAL_ENTITY ||
      (targetOwner?.type === OwnerType.INDIVIDUAL &&
        draggedOwner?.type === OwnerType.INDIVIDUAL &&
        getLevel(target) !== 1)) &&
    !targetOwner.areIndividualsWithDecisiveInfluenceAbsent &&
    !overSitsInIndividual;
  !isCyclicLink(over, newLinks);

  const oldParentLinkId = dragged.parentLinkId;

  const reindex = (arr: TOwnerLink[]) =>
    arr.forEach((l, i) => {
      l.displayOrder = i;
    });

  const siblingsOf = (parentId: string | null) =>
    newLinks
      .filter((l) => l.parentLinkId === parentId && l.id !== dragged.id)
      .sort((a, b) => a.displayOrder - b.displayOrder);

  const insertAsSibling = (of: TOwnerLink, before: boolean) => {
    dragged.parentLinkId = of.parentLinkId;
    dragged.toNodeId = of.toNodeId;
    const siblings = siblingsOf(of.parentLinkId);
    const pos = siblings.findIndex((l) => l.id === of.id);
    siblings.splice(before ? pos : pos + 1, 0, dragged);
    reindex(siblings);
  };

  const canDropAsSiblingOf = (of: TOwnerLink): boolean => {
    if (of.parentLinkId === oldParentLinkId) return true;

    const containingParent = newLinks.find((l) => l.id === of.parentLinkId);
    if (containingParent) {
      return canDropIntoParent(dragged, containingParent, newLinks, owners);
    }

    const rootSiblings = newLinks.filter(
      (l) => l.parentLinkId === of.parentLinkId && l.id !== dragged.id
    );

    if (rootSiblings.some((l) => l.fromNodeId === dragged.fromNodeId)) return false;

    const draggedOwner = owners.find((o) => o.id === dragged.fromNodeId);
    if (draggedOwner && UNIQUE_PER_PARENT_TYPES.includes(draggedOwner.type)) {
      const siblingTypes = rootSiblings.map((l) => owners.find((o) => o.id === l.fromNodeId)?.type);
      if (siblingTypes.includes(draggedOwner.type)) return false;
    }
    if (
      draggedOwner?.type === OwnerType.INDIVIDUAL &&
      newLinks.some((l) => l.parentLinkId === dragged.id)
    ) {
      return false;
    }

    return true;
  };

  if (overIsParent && dragged.parentLinkId === over.id) {
    if (inTopZone) {
      const allowed = canDropAsSiblingOf(over);
      onDropParentChange?.(resolveDisabledHighlight(over.parentLinkId), allowed);
      if (!allowed) return;
      insertAsSibling(over, true);
    } else if (inBottomZone) {
      const allowed = canDropAsSiblingOf(over);
      onDropParentChange?.(resolveDisabledHighlight(over.parentLinkId), allowed);
      if (!allowed) return;
      insertAsSibling(over, false);
    } else {
      onDropParentChange?.(resolveDisabledHighlight(over.id), true);
      return;
    }
  } else if (overIsParent) {
    if (inTopZone) {
      const allowed = canDropAsSiblingOf(over);
      onDropParentChange?.(resolveDisabledHighlight(over.parentLinkId), allowed);
      if (!allowed) return;
      insertAsSibling(over, true);
    } else if (inBottomZone) {
      const allowed = canDropAsSiblingOf(over);
      onDropParentChange?.(resolveDisabledHighlight(over.parentLinkId), allowed);
      if (!allowed) return;
      insertAsSibling(over, false);
    } else {
      const allowed = canDropIntoParent(dragged, over, newLinks, owners);
      onDropParentChange?.(resolveDisabledHighlight(over.id), allowed);
      if (!allowed) return;

      dragged.parentLinkId = over.id;
      dragged.toNodeId = over.fromNodeId;
      const children = siblingsOf(over.id);
      if (pointerInUpperHalf) {
        children.unshift(dragged);
      } else {
        children.push(dragged);
      }
      reindex(children);
    }
  } else {
    const allowed = canDropAsSiblingOf(over);
    onDropParentChange?.(resolveDisabledHighlight(over.parentLinkId), allowed);
    if (!allowed) return;

    const before = inTopZone ? true : inBottomZone ? false : pointerInUpperHalf;
    insertAsSibling(over, before);
  }

  let finalLinks = newLinks;

  if (dragged.parentLinkId !== oldParentLinkId) {
    const oldParentOwnerIndex = owners.findIndex(
      (item) => item.id === links.find((link) => link.id === oldParentLinkId)?.fromNodeId
    );

    reindex(siblingsOf(oldParentLinkId));
    finalLinks = syncClones(newLinks, dragged, oldParentLinkId, owners);
    syncCloneOrder(finalLinks, oldParentLinkId);
    syncCloneOrder(finalLinks, dragged.parentLinkId);

    trigger(`owners.${oldParentOwnerIndex}`);
  } else {
    syncCloneOrder(finalLinks, dragged.parentLinkId);
  }
  const wasAtRoot = oldParentLinkId === null;
  const isAtRoot = dragged.parentLinkId === null;
  const rootCrossed = wasAtRoot !== isAtRoot;

  const changed =
    finalLinks.length !== links.length ||
    finalLinks.some((l) => {
      const orig = links.find((o) => o.id === l.id);
      return !orig || orig.parentLinkId !== l.parentLinkId || orig.displayOrder !== l.displayOrder;
    });

  if (changed) setValue('link', finalLinks, { shouldDirty: true });
  if (changed && rootCrossed) handleCalculateOwnershipPercentage?.();
};

export const collectSubtreeIds = (rootId: string, list: TOwnerLink[]): Set<string> => {
  const ids = new Set<string>([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const l of list) {
      if (l.parentLinkId && ids.has(l.parentLinkId) && !ids.has(l.id)) {
        ids.add(l.id);
        grew = true;
      }
    }
  }
  return ids;
};

const cloneSubtree = (
  root: TOwnerLink,
  list: TOwnerLink[],
  newParentLink: TOwnerLink
): TOwnerLink[] => {
  const result: TOwnerLink[] = [];
  const cloneNode = (node: TOwnerLink, parentLinkId: string | null, toNodeId: string) => {
    const newId = uuidv4();
    result.push({ ...node, id: newId, parentLinkId, toNodeId });
    list
      .filter((l) => l.parentLinkId === node.id)
      .forEach((child) => cloneNode(child, newId, child.toNodeId));
  };
  cloneNode(root, newParentLink.id, newParentLink.fromNodeId);
  return result;
};

export const syncClones = (
  linksArr: TOwnerLink[],
  dragged: TOwnerLink,
  oldParentLinkId: string | null,
  owners: TOwner[]
): TOwnerLink[] => {
  let result = linksArr;

  const oldParentLink = linksArr.find((l) => l.id === oldParentLinkId);
  if (oldParentLink) {
    const otherOldInstances = result.filter(
      (l) => l.id !== oldParentLink.id && areMirrorInstances(l, oldParentLink, result, owners)
    );
    const idsToRemove = new Set<string>();
    for (const inst of otherOldInstances) {
      const mirror = result.find(
        (l) =>
          l.parentLinkId === inst.id && l.fromNodeId === dragged.fromNodeId && l.id !== dragged.id
      );
      if (mirror) {
        collectSubtreeIds(mirror.id, result).forEach((id) => idsToRemove.add(id));
      }
    }
    if (idsToRemove.size) {
      result = result.filter((l) => !idsToRemove.has(l.id));
      for (const inst of otherOldInstances) {
        result
          .filter((l) => l.parentLinkId === inst.id)
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .forEach((l, i) => {
            l.displayOrder = i;
          });
      }
    }
  }

  const newParentLink = result.find((l) => l.id === dragged.parentLinkId);
  if (newParentLink) {
    const otherNewInstances = result.filter(
      (l) => l.id !== newParentLink.id && areMirrorInstances(l, newParentLink, result, owners)
    );
    for (const inst of otherNewInstances) {
      let chain: TOwnerLink | undefined = inst;
      let insideDraggedOwner = false;
      while (chain) {
        if (chain.fromNodeId === dragged.fromNodeId) {
          insideDraggedOwner = true;
          break;
        }
        chain = result.find((l) => l.id === chain!.parentLinkId);
      }
      if (insideDraggedOwner) continue;

      const alreadyThere = result.some(
        (l) => l.parentLinkId === inst.id && l.fromNodeId === dragged.fromNodeId
      );
      if (!alreadyThere) {
        const draggedSubtree = collectSubtreeIds(dragged.id, result);
        if (draggedSubtree.has(inst.id)) continue;
        result = [...result, ...cloneSubtree(dragged, result, inst)];
      }
    }
  }

  return result;
};

export const syncCloneOrder = (linksArr: TOwnerLink[], parentLinkId: string | null) => {
  if (parentLinkId === null) return;

  const parentLink = linksArr.find((l) => l.id === parentLinkId);
  if (!parentLink) return;

  const orderByOwner = new Map<string, number>();
  linksArr
    .filter((l) => l.parentLinkId === parentLink.id)
    .forEach((l) => orderByOwner.set(l.fromNodeId, l.displayOrder));

  linksArr
    .filter((l) => l.fromNodeId === parentLink.fromNodeId && l.id !== parentLink.id)
    .forEach((instance) => {
      linksArr
        .filter((l) => l.parentLinkId === instance.id)
        .forEach((child) => {
          const order = orderByOwner.get(child.fromNodeId);
          if (order !== undefined) child.displayOrder = order;
        });
    });
};

export const canDropIntoParent = (
  draggedLink: TOwnerLink,
  parentLink: TOwnerLink,
  links: TOwnerLink[],
  owners: TOwner[]
): boolean => {
  const draggedOwner = owners.find((o) => o.id === draggedLink.fromNodeId);
  if (!draggedOwner) return false;

  let chain: TOwnerLink | undefined = parentLink;
  while (chain) {
    if (chain.fromNodeId === draggedLink.fromNodeId) return false;
    chain = links.find((l) => l.id === chain!.parentLinkId);
  }

  const existingChildren = links.filter(
    (l) => l.parentLinkId === parentLink.id && l.id !== draggedLink.id
  );

  if (existingChildren.some((l) => l.fromNodeId === draggedLink.fromNodeId)) {
    return false;
  }
  const targetDepth = linkDepth(parentLink, links) + 1;

  if (targetDepth + subtreeHeight(draggedLink.id, links) - 1 > MAX_LINK_LEVEL) {
    return false;
  }
  if (UNIQUE_PER_PARENT_TYPES.includes(draggedOwner.type)) {
    const childTypes = existingChildren.map((l) => owners.find((o) => o.id === l.fromNodeId)?.type);
    if (childTypes.includes(draggedOwner.type)) return false;
  }

  const parentOwner = owners.find((o) => o.id === parentLink.fromNodeId);
  if (parentOwner?.type === OwnerType.INDIVIDUAL) {
    if (draggedOwner.type !== OwnerType.INDIVIDUAL) return false;
    if (links.some((l) => l.parentLinkId === draggedLink.id)) return false;
    if (existingChildren.length >= 1) return false;
  }
  if (draggedOwner.type === OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP) return false;

  return true;
};

export const areMirrorInstances = (
  a: TOwnerLink,
  b: TOwnerLink,
  links: TOwnerLink[],
  owners: TOwner[]
): boolean => {
  if (a.fromNodeId !== b.fromNodeId) return false;

  const owner = owners.find((o) => o.id === a.fromNodeId);
  if (owner?.type === OwnerType.LEGAL_ENTITY) return true;

  const parentA = links.find((l) => l.id === a.parentLinkId);
  const parentB = links.find((l) => l.id === b.parentLinkId);

  if (!parentA && !parentB) return true;
  if (!parentA || !parentB) return false;

  return areMirrorInstances(parentA, parentB, links, owners);
};

const isCyclicLink = (link: TOwnerLink, links: TOwnerLink[]): boolean => {
  let parent = links.find((l) => l.id === link.parentLinkId);
  while (parent) {
    if (parent.fromNodeId === link.fromNodeId) return true;
    parent = links.find((l) => l.id === parent!.parentLinkId);
  }
  return false;
};

export const linkDepth = (link: TOwnerLink, links: TOwnerLink[]): number => {
  let depth = 1;
  let parent = links.find((l) => l.id === link.parentLinkId);
  while (parent) {
    depth++;
    parent = links.find((l) => l.id === parent!.parentLinkId);
  }
  return depth;
};

export const subtreeHeight = (rootId: string, links: TOwnerLink[]): number => {
  const children = links.filter((l) => l.parentLinkId === rootId);
  if (!children.length) return 1;
  return 1 + Math.max(...children.map((c) => subtreeHeight(c.id, links)));
};
